import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import useNotificationWebSocket from "../hooks/useNotificationWebSocket";

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [mustAcceptCGU, setMustAcceptCGU] = useState(false);

  // Callback for new notifications
  const handleNewNotification = useCallback((notification) => {
    // DEV: console.log('New notification received:', notification);
  }, []);

  // WebSocket for real-time notifications
  const { 
    isConnected: wsConnected, 
    unreadCount, 
    resetUnreadCount,
    decrementUnreadCount 
  } = useNotificationWebSocket(token, handleNewNotification);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Check token validity on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        setMustChangePassword(response.data.must_change_password || false);
        // Check CGU for cabinet users (including collaborateurs)
        if (response.data.role === "cabinet" || response.data.role === "collaborateur_cabinet") {
          const cguAccepted = response.data.cgu_accepted || false;
          const privacyAccepted = response.data.privacy_accepted || false;
          setMustAcceptCGU(!(cguAccepted && privacyAccepted));
        }
      } catch (error) {
        // console.error(/* PROD */ "Token validation failed:", error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });
      
      const { access_token, user: userData, must_change_password, must_accept_cgu } = response.data;
      
      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      setMustChangePassword(must_change_password || false);
      setMustAcceptCGU(must_accept_cgu || false);
      
      return { 
        success: true, 
        mustChangePassword: must_change_password,
        mustAcceptCGU: must_accept_cgu
      };
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur de connexion";
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setMustAcceptCGU(false);
    delete axios.defaults.headers.common["Authorization"];
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
    // Update user object as well
    if (user) {
      setUser({ ...user, must_change_password: false });
    }
  };

  const clearMustAcceptCGU = () => {
    setMustAcceptCGU(false);
    // Update user object as well
    if (user) {
      setUser({ ...user, cgu_accepted: true, privacy_accepted: true });
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    mustChangePassword,
    clearMustChangePassword,
    mustAcceptCGU,
    clearMustAcceptCGU,
    isAdmin: user?.role === "admin_igt" || user?.role === "super_admin" || user?.role === "admin_igat",
    isSuperAdmin: user?.role === "admin_igt" || user?.role === "super_admin",
    isCabinet: user?.role === "cabinet" || user?.role === "collaborateur_cabinet",
    isAuditeur: user?.role === "auditeur",
    // WebSocket notifications
    wsConnected,
    unreadCount,
    resetUnreadCount,
    decrementUnreadCount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
